import React from "react";
import { MapPin, MoreVertical, Plus, Download, RefreshCw, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { KIND_META, fmtDate, rowKey, copyText } from "./clientMeta";
import { StatusPill } from "./ClientDetailTabs";

const RowMenu = ({ row, onSelect, onOpenFull }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button type="button" onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-md hover:bg-slate-100 transition-colors"
              aria-label="Client actions" data-testid={`client-row-menu-${row.kind}-${row.id}`}>
        <MoreVertical className="h-4 w-4" style={{ color: "#475569" }} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-44 bg-white" onClick={(e) => e.stopPropagation()}>
      <DropdownMenuItem onClick={() => onSelect(row)}>View details</DropdownMenuItem>
      {row.kind !== "referral_partner" ? <DropdownMenuItem onClick={() => onOpenFull(row)}>Open full profile</DropdownMenuItem> : null}
      <DropdownMenuSeparator />
      {row.phone ? <DropdownMenuItem asChild><a href={`tel:${row.phone}`}>Call {row.phone}</a></DropdownMenuItem> : null}
      {row.email ? <DropdownMenuItem asChild><a href={`mailto:${row.email}`}>Email</a></DropdownMenuItem> : null}
      {row.email ? (
        <DropdownMenuItem onClick={() => copyText(row.email, "Email copied.")}>Copy email</DropdownMenuItem>
      ) : null}
    </DropdownMenuContent>
  </DropdownMenu>
);

const ClientRow = ({ row, active, checked, onCheck, onSelect, onOpenFull }) => {
  const meta = KIND_META[row.kind];
  const Icon = meta.Icon;
  return (
    <li
      onClick={() => onSelect(row)}
      className={`group flex items-center gap-3 px-4 py-3.5 cursor-pointer border-l-[3px] transition-colors ${active ? "bg-blue-50/70 border-l-blue-600" : "border-l-transparent hover:bg-slate-50"}`}
      data-testid={`client-row-${row.kind}-${row.id}`}
      aria-selected={active}
    >
      <Checkbox checked={checked} onCheckedChange={onCheck} onClick={(e) => e.stopPropagation()} aria-label={`Select ${row.name}`}
                data-testid={`client-row-check-${row.kind}-${row.id}`} />
      <span className={`grid place-items-center h-11 w-11 rounded-full shrink-0 ${meta.avatar}`}><Icon className="h-5 w-5" /></span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[15px] truncate" style={{ color: "#0B2545" }}>{row.name || "Unnamed"}</div>
        <span className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 ${meta.badge}`}>{meta.label}</span>
        <div className="text-xs mt-1 flex items-center gap-1 truncate" style={{ color: "#64748B" }}>
          <MapPin className="h-3 w-3 shrink-0" />{row.location || "No location"}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <StatusPill kind={row.kind} status={row.status} testid={`client-row-status-${row.kind}-${row.id}`} />
        <span className="text-[11px] whitespace-nowrap" style={{ color: "#64748B" }}>Last activity: {fmtDate(row.last_activity_at || row.created_at)}</span>
      </div>
      <RowMenu row={row} onSelect={onSelect} onOpenFull={onOpenFull} />
    </li>
  );
};

export default function ClientList({ rows, selectedKey, checked, setChecked, onSelect, onOpenFull, addMenu, onExport, onBulkStatus }) {
  const allChecked = rows.length > 0 && rows.every((r) => checked.has(rowKey(r)));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(rows.map(rowKey)));
  const toggle = (r) => setChecked((prev) => { const n = new Set(prev); n.has(rowKey(r)) ? n.delete(rowKey(r)) : n.add(rowKey(r)); return n; });
  return (
    <div className="flex flex-col h-full" data-testid="clients-list-panel">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b" style={{ borderColor: "#E2E8F0" }}>
        <label className="flex items-center gap-2.5 text-sm font-semibold cursor-pointer" style={{ color: "#0B2545" }}>
          <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="clients-select-all" />Select all
        </label>
        <span className="text-sm" style={{ color: "#64748B" }} data-testid="clients-count">{rows.length} {rows.length === 1 ? "client" : "clients"}</span>
        {addMenu}
      </div>
      {checked.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b bg-[#0B2545] text-white" data-testid="clients-bulk-bar">
          <span className="text-sm font-semibold mr-auto">{checked.size} selected</span>
          <Button size="sm" variant="secondary" className="h-8" onClick={onExport} data-testid="clients-bulk-export"><Download className="h-3.5 w-3.5 mr-1.5" />Export</Button>
          <Button size="sm" variant="secondary" className="h-8" onClick={onBulkStatus} data-testid="clients-bulk-status"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Change status</Button>
          <button type="button" onClick={() => setChecked(new Set())} className="p-1 rounded hover:bg-white/10" aria-label="Clear selection" data-testid="clients-bulk-clear"><X className="h-4 w-4" /></button>
        </div>
      ) : null}
      <ul className="divide-y overflow-y-auto flex-1" style={{ borderColor: "#F1F5F9" }} data-testid="clients-list">
        {rows.map((r) => (
          <ClientRow key={rowKey(r)} row={r} active={selectedKey === rowKey(r)} checked={checked.has(rowKey(r))}
                     onCheck={() => toggle(r)} onSelect={onSelect} onOpenFull={onOpenFull} />
        ))}
      </ul>
    </div>
  );
}

export const AddClientMenu = ({ onPick, big = false }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      {big ? (
        <Button className="h-12 pl-5 pr-4 text-base font-semibold rounded-xl shadow-sm bg-blue-600 hover:bg-blue-700 text-white transition-colors" data-testid="add-client-button">
          <Plus className="h-5 w-5 mr-2" />Add Client<span className="ml-4 pl-3 border-l border-white/30">▾</span>
        </Button>
      ) : (
        <Button variant="outline" size="sm" className="h-9 font-semibold text-blue-700 border-slate-200" data-testid="clients-list-add">
          <Plus className="h-4 w-4 mr-1" />Add
        </Button>
      )}
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56 bg-white">
      {Object.entries(KIND_META).map(([kind, m]) => (
        <DropdownMenuItem key={kind} onClick={() => onPick(kind)} data-testid={`add-client-${kind}${big ? "" : "-list"}`}>
          <m.Icon className="h-4 w-4 mr-2" />{m.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);
